import { DbKey } from '../../../../adl-gen/common/db';
import {
  ActionFieldType,
  ActionOnceFieldType,
  Actioned,
  AlignColumn,
  ItemState,
  ListEntry,
  ListFieldInfo,
  SearchBoxState,
  SortColumn,
  ToggleFieldType,
  ToggleValue
} from './list-types';
import { TranslateFn } from '../../../../internationalization/i18n-hoc';
import { SelectOption } from '../../../../ui/widgets/select/select';
import {
  FieldState,
  FormValue,
  newIntegerFieldState,
  newNonEmptyTextField,
  withValueCallback
} from '../../../../ui/widgets/text-input/text-field';
import { SortDirection } from '../../../../adl-gen/common/tabular';

interface CheckboxChangeParam<T> {
  fieldName: string;
  value: ListEntry<T>;
  oneOff?: boolean;
}
interface ListStoreProps<T = any, R = void> {
  data: ListEntry<T>[];
  // Changed by updating attributes only
  fields: ListFieldInfo<T, R>[];
  // For updates/?creates
  fieldState: Map<DbKey<T>, ItemState<T>>;
  // Special handling for one-off actions to control display
  actioned: Actioned<T>[];
  // Observables updated when props changed
  toggleValues: ToggleValue<T>[];
  sortColumn?: SortColumn;
  // Map of fieldName to SearchBoxState - do we need a map at all?
  searchBoxState: Map<string, SearchBoxState>;

  initialize: (initParams: {
    initFields: ListFieldInfo<T, R>[];
    onSelect?: (id: DbKey<T>) => Promise<void>;
    onChange?: (fieldName: string, item: ListEntry<T>) => (value: FormValue<string | number>) => void;
  }) => ListLocalStore<T, R>;
  sortOnColumn: (existingState: ListLocalStore<T, R>, sort?: SortColumn) => ListLocalStore<T, R>;
  toggleSortOnColumn: (existingState: ListLocalStore<T, R>, idx: number) => ListLocalStore<T, R>;
  onToggle: (existingState: ListLocalStore<T, R>, fieldName: string) => (itemId: DbKey<T>) => ListLocalStore<T, R>;
  onSearch: (fieldName: string) => (value?: SelectOption) => ListLocalStore<T, R>;
  onCheckboxChange: (params: CheckboxChangeParam<T>) => ListLocalStore<T, R>;
  onDelete: (existingState: ListLocalStore<T, R>, value: ListEntry<T>) => ListLocalStore<T, R>;

  getColumnAlignments: () => AlignColumn[];
  getColumnVisibility: (size: 'sm') => ('sm' | undefined)[];
  // getSearchBoxes: () => [string, SearchBoxState][];
  // getToggleValue: (fieldName: string, id: DbKey<T>) => ToggleValue<T> | undefined;
  // getFieldState: (id: DbKey<T>) => ItemState<T> | undefined;
  // isActioned: (itemId: DbKey<T>) => boolean;
}

/** Local storage and manipulation for lists */
export class ListLocalStore<T = any, R = void> implements ListStoreProps<T, R> {
  data: ListEntry<T>[] = [];
  // Changed by updating attributes only
  fields: ListFieldInfo<T, R>[] = [];
  // For updates/?creates
  fieldState: Map<DbKey<T>, ItemState<T>> = new Map();
  // Special handling for one-off actions to control display
  actioned: Actioned<T>[] = [];
  // Updated when props changed
  toggleValues: ToggleValue<T>[] = [];
  sortColumn?: SortColumn;
  // Map of fieldName to SearchBoxState
  searchBoxState: Map<string, SearchBoxState> = new Map();

  constructor(data: ListEntry<T>[], readonly t: TranslateFn, readonly refData?: R) {
    this.data = data;
  }

  //  @action
  // This is *not* a reducer dispatch it's basically a post-constructor op - use _this_ not a clone
  initialize = ({
    initFields,
    onSelect,
    onChange
  }: {
    initFields: ListFieldInfo<T, R>[];
    onSelect?: (id: DbKey<T>) => Promise<void>;
    onChange?: (fieldName: string, item: ListEntry<T>) => (value: FormValue<string | number>) => void;
  }): ListLocalStore<T, R> => {
    // data will already be loaded
    this.fields = setupFields(initFields, onSelect);
    this.fieldState = setupFieldState(this, onChange);
    this.searchBoxState = setupSearchBoxState(this);
    this.toggleValues = setupToggleValueState(this);
    this.actioned = setupActionOnceFieldState(this);
    return this;
  };

  sortOnColumn = (existingState: ListLocalStore<T, R>, sort?: SortColumn): ListLocalStore<T, R> => {
    const { fields, data, t, refData } = existingState;
    let newData = data.slice();
    if (sort !== undefined) {
      fields.forEach((sortField, idx) => {
        // NB Only update data for SORTED columns
        if (idx === sort.index) {
          if ('sortable' in sortField && sortField.sortable && sortField.displayed) {
            switch (sort.sortDirection) {
              case SortDirection.descending:
                {
                  /// Assumes strings not numbers, ASCII sort
                  newData = data.slice().sort((a, b) => sortCompareFn(sortField, a, b, t, refData));
                }
                break;
              case SortDirection.ascending:
                {
                  /// Assumes strings not numbers, ASCII sort
                  newData = data.slice().sort((a, b) => sortCompareFn(sortField, b, a, t, refData));
                }
                break;
              default:
                newData = data.slice().sort((a, b) => {
                  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
                });
            }
          }
        }
      });
    } else {
      newData = data.slice().sort((a, b) => {
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      });
    }
    return { ...existingState, data: newData, sortColumn: sort };
  };

  toggleSortOnColumn = (existingState: ListLocalStore<T, R>, idx: number): ListLocalStore<T, R> => {

    const { fields, sortColumn } = existingState;

    // Do nothing if field is not displayed and sortable
    const field = fields[idx];
    if (!(field.displayed && 'sortable' in field && field.sortable)) {
      return this;
    }

    let newSort: SortColumn|undefined = undefined;

    if (sortColumn === undefined || idx !== sortColumn.index) {
      // Not already set OR 
      // Start cycle on new column
      // Set a sort column
      newSort = { index: idx, sortDirection: SortDirection.ascending };
    } else {
      // Bump to next 
      // Sort toggle goes: Undefined -> Up -> Down -> Undefined
      switch (sortColumn.sortDirection) {
        case SortDirection.ascending:
          newSort = { ...sortColumn, sortDirection: SortDirection.descending };
          break;
        case SortDirection.descending:
          newSort = { ...sortColumn, sortDirection: undefined };
          break;
        default:
          newSort = { ...sortColumn, sortDirection: SortDirection.ascending };
      }
    }

    // Now manipulate the field icons
    const updatedFields = setSortIcons(this, newSort);

    return { ...this, fields: updatedFields, sortColumn: newSort, data: sortDataOnColumn(this, newSort) };
  };

  onToggle = (existingState: ListLocalStore<T, R>, fieldName: string) => (itemId: DbKey<T>): ListLocalStore<T, R> => {
    const dataValue = existingState.data.find(item => item.id === itemId);
    if (dataValue) {
      const newToggleState = existingState.toggleValues.slice()
        .map(entry => {
          if (entry.fieldName === fieldName && entry.id === itemId)
            return { ...entry, value: !entry.value };
          return entry;
        });
      return { ...existingState, toggleValues: newToggleState };
    }
    return existingState;
  };

  onSearch = (fieldName: string) => (value?: SelectOption): ListLocalStore<T, R> => {
    const existingSearchBoxState = this.searchBoxState.get(fieldName);
    if (existingSearchBoxState) {
      const newState: ListLocalStore<T, R> = { ...this };
// TODO:
// This works where the value is not undefined. If however the search box is cleared
// ( => value is undefined), the other field search boxes will already be limited by 
// previous actions, and we would be expected to restore them back to their previous
// set of options. Unfortunately, we don't know what that set is from here, as they
// may be reduced by the action of other search boxes. What to do? Try to restore
// only those items which *would* have been there if we didn't run the filter on a 
// previous invocation? Tricky
      // // Run a filter here
      // const items = filteredItems(this.data, value, existingSearchBoxState.filterField);
      // // Then get the other search boxes (if any...)
      // const otherSearchEntries = Array
      //   .from(this.searchBoxState.entries())
      //   .filter(([k, _]) => (k !== fieldName));
      // // ...and filter them accordingly
      // for (const [k, value] of otherSearchEntries) {
      //   const field = this.fields.find(f => f.name === k);
      //   if (field) {
      //     const newOptionsSuperSet = items
      //         .slice()
      //         .sort((a, b) => searchBoxOptionsSort(this, field, a, b))
      //         .map(setupSearchOptions(this, field))
      //         // unique
      //         .filter((value, index, self) => self.findIndex(item => item.value === value.value) === index);
      //     const newOptions = value.options
      //       .filter(option => newOptionsSuperSet
      //         .map(newOption => newOption.value)
      //         .includes(option.value));
      //     newState.searchBoxState.set(k, { ...value, options: newOptions });
      //   };
      // }
      newState.searchBoxState.set(fieldName, { ...existingSearchBoxState, value });
      return newState;
    }
    return this;
  };

  // Return the value if changed
  onCheckboxChange = ({ fieldName, value, oneOff = false }: CheckboxChangeParam<T>): ListLocalStore<T, R> => {
    let update: boolean | undefined;
    // New state not mutated state
    const toggleValueUpdates = this.toggleValues.slice().map(entry => {
      if (entry.id === value.id && entry.fieldName === fieldName) {
        update = !entry.value;
        return { ...entry, value: update };
      }
      return entry;
    });

    const newState: ListLocalStore<T, R> = { ...this, toggleValues: toggleValueUpdates };
    if (oneOff) {
      onOneOffItemChange(fieldName, newState, value);
    }
    return newState;
  };

  onDelete = (existingState: ListLocalStore<T, R>, value: ListEntry<T>): ListLocalStore<T, R> => {
    // New state not mutated state
    const newData = existingState.data.filter(entry => entry.id !== value.id);
    return { ...existingState, data: newData };
  };

  // Work out alignments
  getColumnAlignments = (): AlignColumn[] => {
    return this.fields.map(field => (field.align === undefined ? 'left' : field.align));
  }

  getColumnVisibility = (size: 'sm'): ('sm' | undefined)[] => {
    return this.fields.map(field => (field.visibleAfterSm ? size : undefined));
  };

  // getSearchBoxes = () => {
  //   return Array.from(this.searchBoxState.entries());
  // }

//   getToggleValue = (fieldName: string, id: DbKey<T>) => {
//     const value = this.toggleValues.find(v => v.fieldName === fieldName && v.id === id);
//     console.log('DEBUG getToggleValue', value);
//     return value;
// }
  // getFieldState = (id: DbKey<T>) => this.fieldState.get(id);

  // isActioned = (itemId: DbKey<T>) =>
  //   this.actioned.find(entry => entry.value.id === itemId && entry.actioned) !== undefined;

// NB No private methods - otherwise we are not properly copying the class with the spread op when updating state

}

const sortDataOnColumn = <T, R>(existingState: ListLocalStore<T, R>, sort?: SortColumn) => {
    const { fields, data } = existingState;
    let newData = data.slice();
    if (sort !== undefined) {
      fields.forEach((sortField, idx) => {
        // NB Only update data for SORTED columns
        if (idx === sort.index) {
          if ('sortable' in sortField && sortField.sortable && sortField.displayed) {
            switch (sort.sortDirection) {
              case SortDirection.descending:
                {
                  /// Assumes strings not numbers, ASCII sort
                  newData = data.slice().sort((a, b) => sortCompareFn(sortField, a, b, existingState.t, existingState.refData));
                }
                break;
              case SortDirection.ascending:
                {
                  /// Assumes strings not numbers, ASCII sort
                  newData = data.slice().sort((a, b) => sortCompareFn(sortField, b, a, existingState.t, existingState.refData));
                }
                break;
              default:
                newData = data.slice().sort((a, b) => {
                  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
                });
            }
          }
        }
      });
    } else {
      newData = data.slice().sort((a, b) => {
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      });
    }
    return newData;
  };


const setSortIcons = <T, R>(state: ListLocalStore<T, R>, sortColumn?: SortColumn): ListFieldInfo<T, R>[] => {
    const { fields } = state;

    if (sortColumn === undefined) {
      // No sort column defined. Set all sortable fields to unsorted and all others to undefined
      return fields.slice().map(field => {
        if ('sortable' in field && field.sortable && field.displayed) {
          return { ...field, sortIconType: 'unsorted' };
        }
        return { ...field, sortIconType: undefined };
      });
    }

    // We have a sort column.
    return fields.slice().map((field, idx) => {
      // Find the non-sortable fields
      if (!('sortable' in field && field.sortable && field.displayed)) {
        return { ...field, sortIconType: undefined };
      }
      // These are sortable fields. If they are not the current sortColumn, set to unsorted
      if (idx !== sortColumn.index) {
        return { ...field, sortIconType: 'unsorted' };
      }
      // This is the sorted column. Bump the icon
      switch (sortColumn.sortDirection) {
        case SortDirection.descending:
          return { ...field, sortIconType: 'down' };
        case SortDirection.ascending:
          return { ...field, sortIconType: 'up' };
        default:
          return { ...field, sortIconType: 'unsorted' };
      }
    })
  };

const onOneOffItemChange = <T, R>(fieldName: string, state: ListLocalStore<T, R>, item: ListEntry<T>) => {
    // New state not mutated state
    const nextList = state.actioned.slice().map(entry => {
      if (entry.value.id === item.id) {
        const field = state.fields.find(f => f.name === fieldName);
        if (field && field.kind === 'action-once') {
          return { ...entry, actioned: field.actioned(item, state.refData) };
        }
      }
      return entry;
    });
    return nextList;
  };

const setupFields = <T, R>(initFields: ListFieldInfo<T, R>[], onSelect?: (id: DbKey<T>) => Promise<void>) => {
    const fields: ListFieldInfo<T, R>[] = [];
    initFields.map(field => {
      const f = field;
      f.sortIconType = 'sortable' in field && field.sortable && field.displayed ? 'unsorted' : undefined;
      switch (f.kind) {
        // If onClick is not separately defined, set it to onSelect. If there's no onSelect, set to undefined
        case 'link':
        case 'i18nTextLink':
        case 'mappedLink':
          {
            if (!f.onClick)
              f.onClick = onSelect || undefined;
          }
          break;
        default:
          break;
      }
      fields.push(f);
    });
    return fields;
  };

const setupFieldState = <T, R>(
    state: ListLocalStore<T, R>, 
    onChange?: (fieldName: string, item: ListEntry<T>) => (value: FormValue<string | number>) => void
  ): Map<DbKey<T>, ItemState<T>> => {
    const fieldState: Map<DbKey<T>, ItemState<T>> = new Map();
    for (const field of state.fields) {
      if (field.kind === 'action' && onChange) {
        for (const item of state.data) {
          fieldState.set(item.id, {
            ...fieldState.get(item.id),
            ...setupActionField(field, item, onChange)
          });
        }
      }
    }
    return fieldState;
  };

const setupSearchBoxState = <T, R>(state: ListLocalStore<T, R>, ): Map<string, SearchBoxState> => {
    const searchBoxState: Map<string, SearchBoxState> = new Map();
    for (const field of state.fields) {
      // First set up search box data
      const filterField = 'filterField' in field && field.filterField ? field.filterField : undefined;
      if ('searchable' in field && field.searchable && field.displayed) {
        searchBoxState.set(field.name, {
          title: field.label,
          filterField,
          options: state.data
            .slice()
            .sort((a, b) => searchBoxOptionsSort(state,field, a, b))
            .map(setupSearchOptions(state, field))
            // unique
            .filter((value, index, self) => self.findIndex(item => item.value === value.value) === index),
          // init
          value: undefined
        });
      }
    }
    return searchBoxState;
  };

const searchBoxOptionsSort = <T, R>(state: ListLocalStore<T, R>, field: ListFieldInfo<T, R>, a: ListEntry<T>, b: ListEntry<T>) => {
    if ('compare' in field && field.compare) {
      return field.compare(a, b);
    }
    switch (field.kind) {
      case 'text':
      case 'link':
        return a[field.name].localeCompare(b[field.name]);
      case 'i18nText':
      case 'i18nTextLink':
        return state.t(a[field.name]).localeCompare(state.t(b[field.name]));
      case 'mapped':
      case 'mappedLink': {
        const [searchOptionA, searchOptionB] = field.searchOption
          ? [field.searchOption(a, state.refData), field.searchOption(b, state.refData)]
          : [undefined, undefined];
        const [compareA, compareB] = [searchOptionA, searchOptionB].map(item =>
          item ? (typeof item.label === 'string' ? item.label : state.t(item.label)) : undefined
        );

        return compareA && compareB
          ? compareA.localeCompare(compareB)
          : `${field.map(a, state.refData)}`.localeCompare(`${field.map(b, state.refData)}`);
      }
      default:
        return 0;
    }
  };

const setupSearchOptions = <T, R>(state: ListLocalStore<T, R>, field: ListFieldInfo<T, R>) => (value: ListEntry<T>): SelectOption => {
    const searchOption =
      'searchOption' in field && field.searchOption ? field.searchOption(value, state.refData) : undefined;
    const searchOptionValue = searchOption
      ? {
          label: typeof searchOption.label === 'string' ? searchOption.label : state.t(searchOption.label),
          value: searchOption.value
        }
      : undefined;
    switch (field.kind) {
      case 'text':
      case 'link':
        return (
          searchOptionValue || {
            // else revert to original behaviour
            label: value[field.name],
            value: value.id
          }
        );
      case 'i18nText':
      case 'i18nTextLink':
        return (
          searchOptionValue || {
            // else revert to original behaviour
            label: state.t(value[field.name]),
            value: value.id
          }
        );
      case 'mapped':
      case 'mappedLink': {
        return (
          searchOptionValue || {
            // else revert to original behaviour
            label: `${field.map(value, state.refData)}`,
            value: value.id
          }
        );
      }
      // TODO: would prefer an assertNever here
      default:
        return { label: 'todo', value: 'todo' };
    }
  };

const setupActionOnceFieldState = <T, R>(state: ListLocalStore<T, R>, ) => {
    let actioned: Actioned<T>[] = [];
    for (const field of state.fields) {
      if (field.kind === 'action-once') {
        actioned = [...actioned, ...setupActionOnceField(state, actioned, field)];
      }
    }
    return actioned;
  };

const setupToggleValueState = <T, R>(state: ListLocalStore<T, R>, ) => {
    let toggleValues: ToggleValue<T>[] = [];
    for (const field of state.fields) {
      if (field.kind === 'toggle') { //} && !toggleValues.map(tv => tv.fieldName).includes(field.name)) {
        const togglesForField = setupTogglesForField(state, field);
        toggleValues = [ ...toggleValues, ...togglesForField ];
      }
    }
    return toggleValues;
  };

const setupActionOnceField = <T, R>(state: ListLocalStore<T, R>, actioned: Actioned<T>[], field: ActionOnceFieldType<T, R>) => {
    /// Implement a click-once action
    for (const item of state.data) {
      actioned.push({
        actioned: field.actioned(item, state.refData),
        value: item
      });
    }
    // Do we need to return this
    return actioned;
  };

const setupActionField = <T, _R>(
    field: ActionFieldType<T>,
    item: ListEntry<T>,
    onChange: (fieldName: string, item: ListEntry<T>) => (value: FormValue<string | number>) => void
  ) => {
    let itemState: FieldState<string> | FieldState<number>;
    if (field.fieldType === 'number') {
      // @todo: parameterize this range
      itemState = withValueCallback(newIntegerFieldState(0, 100), onChange(field.name, item));
      itemState.set(field.read(item) as number);
    } else {
      itemState = withValueCallback(newNonEmptyTextField(), onChange(field.name, item));
      itemState.set(field.read(item) as string);
    }

    return {
      item,
      fieldName: field.name,
      value: itemState
    };
  };

const setupTogglesForField = <T, R>(state: ListLocalStore<T, R>,field: ToggleFieldType<T, R>) => {
    const togglesForField: ToggleValue<T>[] = [];
    for (const item of state.data) {
      togglesForField.push({
        id: item.id,
        fieldName: field.name,
        value: field.toggle ? field.toggle(item, state.refData) : false
      });
    }
    return togglesForField;
  };


/// Returns -1 if a < b, 1 if a > b, and 0 otherwise
const sortCompareFn = <T, R>(
  field: ListFieldInfo<T, R>,
  a: ListEntry<T>,
  b: ListEntry<T>,
  t: TranslateFn,
  refData?: R
): number => {
  /// If there's a compare fn defined in the list, use that
  if ('compare' in field && field.compare) {
    return field.compare(a, b);
  } // otherwise...

  // Coerce to numeric if possible...
  let left: string | number;
  let right: string | number;
  // const { t } = props;
  switch (field.kind) {
    case 'i18nText':
    case 'i18nTextLink':
      left = `${t(a[field.name])}`.toLowerCase();
      right = `${t(b[field.name])}`.toLowerCase();
      break;
    case 'mapped':
    case 'mappedLink':
      const rawLeft = field.map(a, refData);
      const rawRight = field.map(b, refData);

      if (typeof rawLeft === 'string' || typeof rawLeft === 'number') {
        left = `${rawLeft}`.toLowerCase();
      } else if (rawLeft.kind === 'text') {
        left = `${t(rawLeft)}`.toLowerCase();
      } else {
        left = `(invalid sortable field type for ${field.kind}: ${rawLeft.kind})`;
      }
      if (typeof rawRight === 'string' || typeof rawRight === 'number') {
        right = `${rawRight}`.toLowerCase();
      } else if (rawRight.kind === 'text') {
        right = `${t(rawRight)}`.toLowerCase();
      } else {
        right = `(invalid sortable field type for ${field.kind}: ${rawRight.kind})`;
      }
      break;
    default:
      left = `${a[field.name]}`.toLowerCase();
      right = `${b[field.name]}`.toLowerCase();
  }
  // Unary '+' coerces string to numeric if can be interpreted as numeric.
  if (!isNaN(+left)) {
    left = +left;
  }
  if (!isNaN(+right)) {
    right = +right;
  }
  return left < right ? -1 : left > right ? 1 : 0;
};

export const filteredBySearchBoxes = <T, R>(newState: ListLocalStore<T,R>): ListEntry<T>[] => {
    let items: ListEntry<T>[] = newState.data;
    for (const state of newState.searchBoxState.values()) {
      items = filteredItems(items, state.value, state.filterField);
    }
    return items;
  }



export const filteredItems = <E extends ListEntry<T> & { [filterField: string]: string | undefined }, T>(
  allItems: E[],
  selectedItem?: SelectOption,
  filterField?: string | undefined
): E[] => {
  if (selectedItem) {
    const refilteredItems = filterField
      ? allItems.filter(item => item[filterField] === selectedItem.value)
      : allItems.filter(item => item.id === selectedItem.value);
    return refilteredItems;
  }
  return allItems;
};

export const createListLocalStore = <T, R>(
  initFields: ListFieldInfo<T, R>[],
  onSelect?: (id: DbKey<T>) => Promise<void>,
  onChange?: (fieldName: string, item: ListEntry<T>) => (value: FormValue<string | number>) => void
) => ({ data, t, refData }: { data: ListEntry<T>[]; t: TranslateFn; refData?: R }): ListLocalStore<T, R> => {
  const store = new ListLocalStore(data, t, refData);
  store.initialize({ initFields, onSelect, onChange });
  return store;
};



// WEBPACK FOOTER //
// ./src/admin/ui/common/list/list-local-store.ts